import React, { useState, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TextField,
    Typography,
    InputAdornment,
    Button,
    IconButton,
    Tooltip,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Grid,
    MenuItem,
    Link,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import {
    FormatBold,
    FormatItalic,
    FormatUnderlined,
    FormatListBulleted,
    FormatListNumbered,
    FormatClear,
} from '@mui/icons-material';
import { getToken } from '../../services/authService';
import { apiClient } from '../../api/apiClient';

const BUNDESLAENDER = [
    { code: "BWB", name: "Baden-Württemberg" },
    { code: "FSB", name: "Bayern" },
    { code: "BLN", name: "Berlin" },
    { code: "BRB", name: "Brandenburg" },
    { code: "BRE", name: "Bremen" },
    { code: "HAM", name: "Hamburg" },
    { code: "HES", name: "Hessen" },
    { code: "MVP", name: "Mecklenburg-Vorpommern" },
    { code: "NSB", name: "Niedersachsen" },
    { code: "NRW", name: "Nordrhein-Westfalen" },
    { code: "RPF", name: "Rheinland-Pfalz" },
    { code: "SAA", name: "Saarland" },
    { code: "FSS", name: "Sachsen" },
    { code: "SAN", name: "Sachsen-Anhalt" },
    { code: "SHS", name: "Schleswig-Holstein" },
    { code: "FST", name: "Thüringen" },
    { code: "BRD", name: "Bundesrepublik Deutschland" }
];

const INSTANZEN = [
    { code: "BSG", name: "Bundessozialgericht" },
    { code: "LSG", name: "Landessozialgericht" },
    { code: "SG", name: "Sozialgericht" }
];

// Einfacher TipTap Editor für Adressen
const SimpleAddressEditor = ({ content, onChange, label }) => {
    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                // Nur die grundlegenden Features für Adressen
                heading: false,
                blockquote: false,
                codeBlock: false,
                horizontalRule: false,
            }),
            Underline,
        ],
        content: content || '',
        onUpdate: ({ editor }) => {
            onChange(editor.getHTML());
        },
    });

    const handleButtonClick = (callback) => (e) => {
        e.preventDefault();
        callback();
    };

    if (!editor) {
        return null;
    }

    return (
        <Paper 
            sx={{ 
                border: '1px solid rgba(0, 0, 0, 0.23)',
                borderRadius: 1,
                mb: 2,
            }}
        >
            {/* Toolbar */}
            <Box sx={{ 
                p: 1, 
                borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                display: 'flex',
                gap: 1,
                alignItems: 'center'
            }}>
                <ToggleButtonGroup size="small">
                    <ToggleButton
                        value="bold"
                        selected={editor.isActive('bold')}
                        onMouseDown={handleButtonClick(() => editor.chain().focus().toggleBold().run())}
                    >
                        <FormatBold />
                    </ToggleButton>
                    <ToggleButton
                        value="italic"
                        selected={editor.isActive('italic')}
                        onMouseDown={handleButtonClick(() => editor.chain().focus().toggleItalic().run())}
                    >
                        <FormatItalic />
                    </ToggleButton>
                    <ToggleButton
                        value="underline"
                        selected={editor.isActive('underline')}
                        onMouseDown={handleButtonClick(() => editor.chain().focus().toggleUnderline().run())}
                    >
                        <FormatUnderlined />
                    </ToggleButton>
                </ToggleButtonGroup>

                <ToggleButtonGroup size="small">
                    <ToggleButton
                        value="bulletList"
                        selected={editor.isActive('bulletList')}
                        onMouseDown={handleButtonClick(() => editor.chain().focus().toggleBulletList().run())}
                    >
                        <FormatListBulleted />
                    </ToggleButton>
                    <ToggleButton
                        value="orderedList"
                        selected={editor.isActive('orderedList')}
                        onMouseDown={handleButtonClick(() => editor.chain().focus().toggleOrderedList().run())}
                    >
                        <FormatListNumbered />
                    </ToggleButton>
                </ToggleButtonGroup>

                <ToggleButton
                    value="clear"
                    onMouseDown={handleButtonClick(() => editor.chain().focus().clearNodes().unsetAllMarks().run())}
                >
                    <FormatClear />
                </ToggleButton>
            </Box>

            {/* Editor */}
            <Box sx={{
                '.ProseMirror': {
                    minHeight: '120px',
                    padding: '12px',
                    backgroundColor: '#fff',
                    '&:focus': {
                        outline: 'none',
                    },
                    '& p': {
                        margin: '4px 0',
                    },
                    '& ul, & ol': {
                        paddingLeft: '20px',
                        margin: '4px 0',
                    },
                },
            }}>
                <EditorContent editor={editor} />
            </Box>
        </Paper>
    );
};

const GerichteList = () => {
    const [allGerichte, setAllGerichte] = useState([]); // Alle Gerichte
    const [filteredGerichte, setFilteredGerichte] = useState([]); // Gefilterte Liste
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchOrt, setSearchOrt] = useState('');
    const [searchBundesland, setSearchBundesland] = useState('');
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedGericht, setSelectedGericht] = useState(null);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const initialLoadDone = React.useRef(false);
    const [editData, setEditData] = useState({
        bezeichnung: '',
        bundesland: '',
        instanz: '',
        bezirk: '',
        kontakt: '',
        pressestelle: '',
        homepage: '',
        hausanschrift: '',
        postanschrift: '',
        ort: '',
        latitude: '',
        longitude: '',
        gerichtsleitung: '',
        erreichbarkeit: ''
    });

    // Einmaliges Laden aller Gerichte
    const fetchGerichte = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await apiClient.get(
                '/gerichte/',
                {
                    headers: {
                        'Authorization': `Bearer ${getToken()}`
                    }
                }
            );
            
            if (response.data && Array.isArray(response.data)) {
                setAllGerichte(response.data);
                setFilteredGerichte(response.data);
            } else {
                setAllGerichte([]);
                setFilteredGerichte([]);
                setError('Unerwartetes Datenformat von der API');
            }
        } catch (error) {
            console.error('Fehler beim Laden der Gerichte:', error);
            setAllGerichte([]);
            setFilteredGerichte([]);
            setError(error.response?.data?.detail || 'Fehler beim Laden der Gerichte');
        } finally {
            setLoading(false);
        }
    };

    // Initiales Laden - nur einmal beim Mounten
    useEffect(() => {
        if (!initialLoadDone.current) {
            initialLoadDone.current = true;
            fetchGerichte();
        }
    }, []);

    // Filtern der Gerichte basierend auf den Suchkriterien
    useEffect(() => {
        const filtered = allGerichte.filter(gericht => {
            const matchesName = gericht.bezeichnung?.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesOrt = gericht.ort?.toLowerCase().includes(searchOrt.toLowerCase());
            const matchesBundesland = !searchBundesland || gericht.bundesland === searchBundesland;
            
            return matchesName && matchesOrt && matchesBundesland;
        });
        
        setFilteredGerichte(filtered);
        setPage(0); // Zurück zur ersten Seite bei Filteränderung
    }, [allGerichte, searchTerm, searchOrt, searchBundesland]);

    // Pagination Handler
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleEditSave = async () => {
        if (!selectedGericht) return;

        try {
            await apiClient.put(
                `/gerichte/${selectedGericht.id}`,
                editData,
                {
                    headers: {
                        'Authorization': `Bearer ${getToken()}`
                    }
                }
            );
            setEditDialogOpen(false);
            setSelectedGericht(null);
            fetchGerichte(); // Lade alle Gerichte neu
        } catch (error) {
            console.error('Fehler beim Aktualisieren des Gerichts:', error);
            setError(error.response?.data?.detail || 'Fehler beim Aktualisieren des Gerichts');
        }
    };

    const handleDeleteConfirm = async () => {
        if (!selectedGericht) return;

        try {
            await apiClient.delete(
                `/gerichte/${selectedGericht.id}`,
                {
                    headers: {
                        'Authorization': `Bearer ${getToken()}`
                    }
                }
            );
            setDeleteDialogOpen(false);
            setSelectedGericht(null);
            fetchGerichte(); // Lade alle Gerichte neu
        } catch (error) {
            console.error('Fehler beim Löschen des Gerichts:', error);
            setError(error.response?.data?.detail || 'Fehler beim Löschen des Gerichts');
        }
    };

    // Berechne die aktuelle Seite der gefilterten Daten
    const paginatedGerichte = filteredGerichte.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    const handleEditClick = (gericht) => {
        setSelectedGericht(gericht);
        setEditData({
            bezeichnung: gericht.bezeichnung || '',
            bundesland: gericht.bundesland || '',
            instanz: gericht.instanz || '',
            bezirk: gericht.bezirk || '',
            kontakt: gericht.kontakt || '',
            pressestelle: gericht.pressestelle || '',
            homepage: gericht.homepage || '',
            hausanschrift: gericht.hausanschrift || '',
            postanschrift: gericht.postanschrift || '',
            ort: gericht.ort || '',
            latitude: gericht.latitude || '',
            longitude: gericht.longitude || '',
            gerichtsleitung: gericht.gerichtsleitung || '',
            erreichbarkeit: gericht.erreichbarkeit || ''
        });
        setEditDialogOpen(true);
    };

    const handleEditChange = (field) => (event) => {
        if (field === 'hausanschrift' || field === 'postanschrift') {
            setEditData(prev => ({
                ...prev,
                [field]: event // TipTap Editor passes the HTML value directly
            }));
        } else {
            setEditData(prev => ({
                ...prev,
                [field]: event.target.value
            }));
        }
    };

    const handleEditCancel = () => {
        setEditDialogOpen(false);
        setSelectedGericht(null);
    };

    const handleDeleteClick = (gericht) => {
        setSelectedGericht(gericht);
        setDeleteDialogOpen(true);
    };

    const handleAddClick = () => {
        setEditData({
            bezeichnung: '',
            bundesland: '',
            instanz: '',
            bezirk: '',
            kontakt: '',
            pressestelle: '',
            homepage: '',
            hausanschrift: '',
            postanschrift: '',
            ort: '',
            latitude: '',
            longitude: '',
            gerichtsleitung: '',
            erreichbarkeit: ''
        });
        setAddDialogOpen(true);
    };

    const handleAddSave = async () => {
        try {
            await apiClient.post(
                '/gerichte/',
                editData,
                {
                    headers: {
                        'Authorization': `Bearer ${getToken()}`
                    }
                }
            );
            setAddDialogOpen(false);
            fetchGerichte(); // Lade alle Gerichte neu
        } catch (error) {
            console.error('Fehler beim Erstellen des Gerichts:', error);
            setError(error.response?.data?.detail || 'Fehler beim Erstellen des Gerichts');
        }
    };

    if (loading) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography>Lade Gerichte...</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ 
            m: 0, 
            p: 0,
            backgroundColor: 'background.default',
            minHeight: '100vh'
        }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" gutterBottom>
                    Gerichte verwalten
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleAddClick}
                >
                    Neues Gericht
                </Button>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            <Paper sx={{ width: '100%', mb: 2 }}>
                <Box sx={{ p: 2 }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                label="Name des Gerichts"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                label="Ort"
                                value={searchOrt}
                                onChange={(e) => setSearchOrt(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                select
                                label="Bundesland"
                                value={searchBundesland}
                                onChange={(e) => setSearchBundesland(e.target.value)}
                            >
                                <MenuItem value="">Alle</MenuItem>
                                {BUNDESLAENDER.map((land) => (
                                    <MenuItem key={land.code} value={land.code}>
                                        {land.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                    </Grid>
                </Box>

                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name des Gerichts</TableCell>
                                <TableCell>Homepage</TableCell>
                                <TableCell>Ort</TableCell>
                                <TableCell>Bundesland</TableCell>
                                <TableCell align="right">Aktionen</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedGerichte.map((gericht) => (
                                <TableRow key={gericht.id}>
                                    <TableCell>{gericht.bezeichnung}</TableCell>
                                    <TableCell>
                                        {gericht.homepage && (
                                            <Link href={gericht.homepage} target="_blank" rel="noopener noreferrer">
                                                {gericht.homepage}
                                            </Link>
                                        )}
                                    </TableCell>
                                    <TableCell>{gericht.ort}</TableCell>
                                    <TableCell>
                                        {BUNDESLAENDER.find(land => land.code === gericht.bundesland)?.name || gericht.bundesland}
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="Bearbeiten">
                                            <IconButton onClick={() => handleEditClick(gericht)}>
                                                <EditIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Löschen">
                                            <IconButton onClick={() => handleDeleteClick(gericht)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    rowsPerPageOptions={[10, 25, 50]}
                    component="div"
                    count={filteredGerichte.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage="Zeilen pro Seite:"
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} von ${count}`}
                />
            </Paper>

            {/* Lösch-Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
            >
                <DialogTitle>Gericht löschen</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Sind Sie sicher, dass Sie das Gericht "{selectedGericht?.bezeichnung}" löschen möchten?
                        {selectedGericht?.entscheidungen_count > 0 && (
                            <Typography color="error" sx={{ mt: 2 }}>
                                Achtung: Dieses Gericht hat noch verknüpfte Entscheidungen und kann nicht gelöscht werden.
                            </Typography>
                        )}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Abbrechen</Button>
                    <Button 
                        onClick={handleDeleteConfirm} 
                        color="error"
                        disabled={selectedGericht?.entscheidungen_count > 0}
                    >
                        Löschen
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Bearbeiten-Dialog */}
            <Dialog
                open={editDialogOpen}
                onClose={handleEditCancel}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    {selectedGericht ? `Gericht "${selectedGericht.bezeichnung}" bearbeiten` : 'Gericht bearbeiten'}
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                required
                                fullWidth
                                label="Bezeichnung"
                                value={editData.bezeichnung}
                                onChange={handleEditChange('bezeichnung')}
                                margin="normal"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                required
                                fullWidth
                                select
                                label="Bundesland"
                                value={editData.bundesland}
                                onChange={handleEditChange('bundesland')}
                                margin="normal"
                            >
                                {BUNDESLAENDER.map((land) => (
                                    <MenuItem key={land.code} value={land.code}>
                                        {land.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                required
                                fullWidth
                                select
                                label="Instanz"
                                value={editData.instanz}
                                onChange={handleEditChange('instanz')}
                                margin="normal"
                            >
                                {INSTANZEN.map((instanz) => (
                                    <MenuItem key={instanz.code} value={instanz.code}>
                                        {instanz.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Bezirk"
                                value={editData.bezirk}
                                onChange={handleEditChange('bezirk')}
                                margin="normal"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="subtitle1" sx={{ mb: 1, mt: 2 }}>Hausanschrift</Typography>
                            <SimpleAddressEditor
                                content={editData.hausanschrift}
                                onChange={handleEditChange('hausanschrift')}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="subtitle1" sx={{ mb: 1 }}>Postanschrift</Typography>
                            <SimpleAddressEditor
                                content={editData.postanschrift}
                                onChange={handleEditChange('postanschrift')}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Ort"
                                value={editData.ort}
                                onChange={handleEditChange('ort')}
                                margin="normal"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Kontakt"
                                value={editData.kontakt}
                                onChange={handleEditChange('kontakt')}
                                margin="normal"
                                multiline
                                rows={2}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Pressestelle"
                                value={editData.pressestelle}
                                onChange={handleEditChange('pressestelle')}
                                margin="normal"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Homepage"
                                value={editData.homepage}
                                onChange={handleEditChange('homepage')}
                                margin="normal"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Latitude"
                                value={editData.latitude}
                                onChange={handleEditChange('latitude')}
                                margin="normal"
                                type="number"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Longitude"
                                value={editData.longitude}
                                onChange={handleEditChange('longitude')}
                                margin="normal"
                                type="number"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Gerichtsleitung"
                                value={editData.gerichtsleitung}
                                onChange={handleEditChange('gerichtsleitung')}
                                margin="normal"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Erreichbarkeit"
                                value={editData.erreichbarkeit}
                                onChange={handleEditChange('erreichbarkeit')}
                                margin="normal"
                                multiline
                                rows={2}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleEditCancel}>Abbrechen</Button>
                    <Button onClick={handleEditSave} variant="contained" color="primary">
                        Speichern
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Neues Gericht Dialog */}
            <Dialog
                open={addDialogOpen}
                onClose={() => setAddDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Neues Gericht hinzufügen</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                required
                                fullWidth
                                label="Bezeichnung"
                                value={editData.bezeichnung}
                                onChange={handleEditChange('bezeichnung')}
                                margin="normal"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                required
                                fullWidth
                                select
                                label="Bundesland"
                                value={editData.bundesland}
                                onChange={handleEditChange('bundesland')}
                                margin="normal"
                            >
                                {BUNDESLAENDER.map((land) => (
                                    <MenuItem key={land.code} value={land.code}>
                                        {land.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                required
                                fullWidth
                                select
                                label="Instanz"
                                value={editData.instanz}
                                onChange={handleEditChange('instanz')}
                                margin="normal"
                            >
                                {INSTANZEN.map((instanz) => (
                                    <MenuItem key={instanz.code} value={instanz.code}>
                                        {instanz.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Bezirk"
                                value={editData.bezirk}
                                onChange={handleEditChange('bezirk')}
                                margin="normal"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="subtitle1" sx={{ mb: 1, mt: 2 }}>Hausanschrift</Typography>
                            <SimpleAddressEditor
                                content={editData.hausanschrift}
                                onChange={handleEditChange('hausanschrift')}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="subtitle1" sx={{ mb: 1 }}>Postanschrift</Typography>
                            <SimpleAddressEditor
                                content={editData.postanschrift}
                                onChange={handleEditChange('postanschrift')}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Ort"
                                value={editData.ort}
                                onChange={handleEditChange('ort')}
                                margin="normal"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Kontakt"
                                value={editData.kontakt}
                                onChange={handleEditChange('kontakt')}
                                margin="normal"
                                multiline
                                rows={2}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Pressestelle"
                                value={editData.pressestelle}
                                onChange={handleEditChange('pressestelle')}
                                margin="normal"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Homepage"
                                value={editData.homepage}
                                onChange={handleEditChange('homepage')}
                                margin="normal"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Latitude"
                                value={editData.latitude}
                                onChange={handleEditChange('latitude')}
                                margin="normal"
                                type="number"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Longitude"
                                value={editData.longitude}
                                onChange={handleEditChange('longitude')}
                                margin="normal"
                                type="number"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Gerichtsleitung"
                                value={editData.gerichtsleitung}
                                onChange={handleEditChange('gerichtsleitung')}
                                margin="normal"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Erreichbarkeit"
                                value={editData.erreichbarkeit}
                                onChange={handleEditChange('erreichbarkeit')}
                                margin="normal"
                                multiline
                                rows={2}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddDialogOpen(false)}>Abbrechen</Button>
                    <Button onClick={handleAddSave} variant="contained" color="primary">
                        Speichern
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default GerichteList; 